/**
 * Reusable React ErrorBoundary — catches render errors in child tree.
 *
 * Usage: <ErrorBoundary fallback={<p>Something broke</p>}>...</ErrorBoundary>
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { translate } from "@/i18n";
import { uiMessages } from "@/i18n/messages/ui";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) return this.props.fallback;
      return (
        <div
          className="rounded-control border p-appkit-md"
          data-testid="error-boundary-fallback"
        >
          <p className="text-appkit-caption text-danger">
            {translate(uiMessages, "sectionFailed")}
          </p>
          <pre className="scrollbar-none mt-appkit-xs max-h-24 overflow-auto text-appkit-mini text-muted">
            {this.state.error?.message}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}
